/**
 * ClickDiagnostics - Click diagnostic utilities for debugging text issues
 * 
 * ✅ OPTION A IMPLEMENTATION
 * 
 * These utilities help diagnose text-related issues during click handling.
 * They use the authoritative text accessor for accurate diagnostics.
 */

import { getCharacterType } from './AuthoritativeTextProvider';
import { InputLogger } from '../../../../../../utils/loggers/InputLogger';

// ============================================================================
// Types
// ============================================================================

export interface ClickDiagnosticInfo {
  /** The grapheme index that was clicked */
  graphemeIndex: number;
  /** The page index where the click occurred */
  pageIndex: number;
  /** World coordinates of the click */
  worldPos: { x: number; y: number };
  /** Character at the clicked position */
  charAtClick: string;
  /** Character type (human readable) */
  charType: string;
  /** Character before the clicked position */
  charBefore: string;
  /** Character type before */
  charTypeBefore: string;
  /** Character after the clicked position */
  charAfter: string;
  /** Character type after */
  charTypeAfter: string;
  /** Total text length */
  textLength: number;
  /** Number of form feeds in text */
  formFeedCount: number;
  /** Whether the text came from WASM */
  isFromWasm: boolean;
  /** Timestamp */
  timestamp: number;
}

export interface ClickDiagnosticDependencies {
  /** The authoritative text */
  text: string;
  /** Whether this text is from WASM */
  isFromWasm: boolean;
  /** The grapheme index from click resolution */
  graphemeIndex: number;
  /** The page index */
  pageIndex: number;
  /** World X coordinate */
  worldX: number;
  /** World Y coordinate */
  worldY: number;
}

// ============================================================================
// Diagnostic Functions
// ============================================================================

/**
 * Create a diagnostic info object for a click event.
 * Uses authoritative text for accurate character identification.
 * 
 * @param deps - Click diagnostic dependencies
 * @returns Full diagnostic information
 */
export function createClickDiagnostics(deps: ClickDiagnosticDependencies): ClickDiagnosticInfo {
  const { text, isFromWasm, graphemeIndex, pageIndex, worldX, worldY } = deps;
  
  const charAtClick = graphemeIndex >= 0 && graphemeIndex < text.length 
    ? text.charAt(graphemeIndex) 
    : '';
  const charBefore = graphemeIndex > 0 
    ? text.charAt(graphemeIndex - 1) 
    : '';
  const charAfter = graphemeIndex < text.length - 1 
    ? text.charAt(graphemeIndex + 1) 
    : '';
  
  return {
    graphemeIndex,
    pageIndex,
    worldPos: { x: worldX, y: worldY },
    charAtClick,
    charType: getCharacterType(charAtClick || null),
    charBefore,
    charTypeBefore: getCharacterType(charBefore || null),
    charAfter,
    charTypeAfter: getCharacterType(charAfter || null),
    textLength: text.length,
    formFeedCount: (text.match(/\f/g) || []).length,
    isFromWasm,
    timestamp: Date.now()
  };
}

/**
 * Log click diagnostics to console.
 * 
 * DEPRECATED: This function is now disabled. Click diagnostics are consolidated
 * in ClickEventCollector which emits a single comprehensive log per click.
 * 
 * @param info - The click diagnostic info
 * @param context - Optional context string for the log
 */
export function logClickDiagnostics(info: ClickDiagnosticInfo, context?: string): void {
  // DISABLED: Consolidated in ClickEventCollector
  // InputLogger.clickDiag.characterAtClick({
  //   graphemeIndex: info.graphemeIndex,
  //   charAtGrapheme: info.charType,
  //   charBefore: info.charTypeBefore,
  //   charAfter: info.charTypeAfter,
  //   pageIndex: info.pageIndex,
  //   worldPos: `(${info.worldPos.x.toFixed(2)}, ${info.worldPos.y.toFixed(2)})`,
  //   textLength: info.textLength,
  //   formFeedCount: info.formFeedCount,
  //   isFromWasm: info.isFromWasm,
  //   timestamp: info.timestamp
  // }, context);
}

/**
 * Create a compact diagnostic string for logging.
 * 
 * @param info - The click diagnostic info
 * @returns Compact string representation
 */
export function formatClickDiagnostics(info: ClickDiagnosticInfo): string {
  return `Click@${info.graphemeIndex} (${info.charType}) on page ${info.pageIndex}, text=${info.textLength} chars, wasm=${info.isFromWasm}`;
}

/**
 * Validate that a click position is within bounds.
 * Returns a warning message if out of bounds, or null if valid.
 * 
 * @param graphemeIndex - The resolved grapheme index
 * @param textLength - The authoritative text length
 * @returns Warning message or null
 */
export function validateClickPosition(
  graphemeIndex: number,
  textLength: number
): string | null {
  if (graphemeIndex < 0) {
    return `Click position ${graphemeIndex} is negative`;
  }
  if (graphemeIndex > textLength) {
    return `Click position ${graphemeIndex} exceeds text length ${textLength}`;
  }
  return null;
}

/**
 * Check if a click is on a special character (page break, newline, etc.)
 * 
 * @param text - The authoritative text
 * @param graphemeIndex - The grapheme index
 * @returns Object describing if this is a special character click
 */
export function checkSpecialCharacterClick(
  text: string,
  graphemeIndex: number
): { isSpecial: boolean; type: string | null; warning?: string } {
  if (graphemeIndex < 0 || graphemeIndex >= text.length) {
    return { isSpecial: false, type: null };
  }
  
  const char = text.charAt(graphemeIndex);
  
  if (char === '\f') {
    return { 
      isSpecial: true, 
      type: 'FORM_FEED',
      warning: 'Click on page break character - operations may affect page structure'
    };
  }
  
  if (char === '\n') {
    return { 
      isSpecial: true, 
      type: 'NEWLINE',
      warning: 'Click on newline character - delete will merge lines'
    };
  }
  
  return { isSpecial: false, type: null };
}
